import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShoppingCart, Loader2, Package, FileSpreadsheet, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const num = (n) => new Intl.NumberFormat("es-MX", { maximumFractionDigits: 2 }).format(n || 0);
const money = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 }).format(n || 0);
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const now = new Date();
const YEARS = Array.from({ length: 7 }, (_, i) => now.getFullYear() - i);
const ESTADOS = { purchase: "Orden de compra", done: "Bloqueada", draft: "Borrador", sent: "Enviada", cancel: "Cancelada" };

export default function ComprasGas() {
  const { currentStructure, structureKey, node } = useGlobal();
  const [mes, setMes] = useState(String(now.getMonth() + 1));
  const [anio, setAnio] = useState(String(now.getFullYear()));

  const { data, isFetching } = useQuery({
    queryKey: ["compras-gas", structureKey, node?.id, mes, anio],
    queryFn: async () => (await api.get("/compras/gas", { params: { structure: structureKey, node_id: node?.id, mes, anio } })).data,
    enabled: !!structureKey,
    keepPreviousData: true,
  });
  const rows = data?.rows || [];

  const grupos = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.referencia || "—";
      if (!map.has(key)) {
        map.set(key, {
          referencia: key, fecha: r.fecha, proveedor: r.proveedor,
          empresa: r.empresa, estado: r.estado, lineas: [], subtotal: 0, subtotal_ord: 0, subtotal_imp: 0,
        });
      }
      const g = map.get(key);
      g.lineas.push(r);
      g.subtotal += r.cantidad || 0;
      g.subtotal_ord += r.ordenada || 0;
      g.subtotal_imp += r.importe || 0;
    }
    return Array.from(map.values());
  }, [rows]);

  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/compras/gas/export", {
        params: { structure: structureKey, node_id: node?.id, mes, anio },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compras_gas_${anio}_${mes}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="compras-gas-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">Contabilidad</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight flex items-center gap-2">
          <ShoppingCart className="w-8 h-8 text-red-500" /> Compras de Gas
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Órdenes de compra de Gas LP · {currentStructure?.name || "—"} ·{" "}
          <span className="text-base font-semibold text-slate-200" data-testid="compras-sucursal">{node?.name || "Toda la estructura"}</span>
        </p>
      </div>

      <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <Select value={mes} onValueChange={setMes}>
          <SelectTrigger className="h-10 bg-slate-950/60 border-slate-700 text-white w-44" data-testid="compras-mes"><SelectValue placeholder="Mes" /></SelectTrigger>
          <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-h-72">
            <SelectItem value="all">Todo el año</SelectItem>
            {MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={anio} onValueChange={setAnio}>
          <SelectTrigger className="h-10 bg-slate-950/60 border-slate-700 text-white w-32" data-testid="compras-anio"><SelectValue placeholder="Año" /></SelectTrigger>
          <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200">
            <SelectItem value="all">Todos</SelectItem>
            {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        <div className="ml-auto flex flex-wrap gap-3 items-center">
          <Button onClick={exportExcel} disabled={exporting || !rows.length} data-testid="compras-export-btn"
            className="group relative h-10 overflow-hidden rounded-lg bg-emerald-700 px-5 !text-white font-medium tracking-wide ring-1 ring-inset ring-emerald-500/30 shadow-sm transition-all duration-300 hover:bg-emerald-600 hover:ring-emerald-400/40 active:translate-y-px disabled:opacity-40 disabled:ring-white/10">
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            <span className="relative flex items-center gap-2 text-white">
              {exporting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <FileSpreadsheet className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5" />}
              <span>{exporting ? "Generando…" : "Exportar a Excel"}</span>
              {!exporting && <Download className="w-3.5 h-3.5 opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />}
            </span>
          </Button>
          <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-slate-900/50 px-5 py-3 shadow-sm" data-testid="compras-kpi-ordenes">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-500/10 ring-1 ring-inset ring-slate-400/15">
              <ShoppingCart className="h-5 w-5 text-slate-300" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Órdenes</p>
              <p className="text-2xl font-semibold leading-tight text-slate-100 tabular-nums">{num(grupos.length)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-slate-900/50 px-5 py-3 shadow-sm" data-testid="compras-kpi-total">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/20">
              <Package className="h-5 w-5 text-emerald-400/90" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Total recibido</p>
              <p className="text-2xl font-semibold leading-tight text-slate-100 tabular-nums">
                {num(data?.total_qty)} <span className="text-xs font-medium text-slate-500">kg</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#131C2E] border border-[#223048] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#223048] hover:bg-transparent">
                <TableHead className="text-slate-400">Fecha confirmación</TableHead>
                <TableHead className="text-slate-400">Referencia</TableHead>
                <TableHead className="text-slate-400">Proveedor</TableHead>
                <TableHead className="text-slate-400">Producto</TableHead>
                <TableHead className="text-slate-400 text-right">Cantidad</TableHead>
                <TableHead className="text-slate-400 text-right">Cantidad recibida</TableHead>
                <TableHead className="text-slate-400">UdM</TableHead>
                <TableHead className="text-slate-400 text-right">Total ($)</TableHead>
                <TableHead className="text-slate-400">Estado</TableHead>
                <TableHead className="text-slate-400">Empresa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grupos.map((g, gi) => (
                <TableRow key={`${g.referencia}-${gi}`} className="border-[#223048] hover:bg-slate-900/40" data-testid={`compra-grupo-${gi}`}>
                  <TableCell className="text-slate-300 tabular-nums">{g.fecha || "—"}</TableCell>
                  <TableCell className="text-white font-semibold">{g.referencia}</TableCell>
                  <TableCell className="text-slate-300">{g.proveedor || "—"}</TableCell>
                  <TableCell className="text-slate-500 text-xs italic">{g.lineas.length} línea{g.lineas.length !== 1 ? "s" : ""}</TableCell>
                  <TableCell className="text-right text-slate-200 font-medium tabular-nums" data-testid={`compra-cantidad-${gi}`}>{num(g.subtotal_ord)}</TableCell>
                  <TableCell className="text-right text-emerald-300 font-semibold tabular-nums" data-testid={`compra-subtotal-${gi}`}>{num(g.subtotal)}</TableCell>
                  <TableCell className="text-slate-400">{g.lineas[0]?.udm || "—"}</TableCell>
                  <TableCell className="text-right text-amber-300 font-semibold tabular-nums" data-testid={`compra-importe-${gi}`}>{money(g.subtotal_imp)}</TableCell>
                  <TableCell className="text-slate-400">{ESTADOS[g.estado] || g.estado || "—"}</TableCell>
                  <TableCell className="text-slate-300">{g.empresa || "—"}</TableCell>
                </TableRow>
              ))}
              {grupos.length > 0 && (
                <TableRow className="border-t-2 border-[#223048] bg-[#0F172A]" data-testid="compra-total-row">
                  <TableCell colSpan={4} className="text-white font-bold uppercase tracking-wide text-xs">Total general · {grupos.length} orden{grupos.length !== 1 ? "es" : ""}</TableCell>
                  <TableCell className="text-right text-slate-100 font-bold tabular-nums text-base">{num(data?.total_ord)}</TableCell>
                  <TableCell className="text-right text-emerald-400 font-extrabold tabular-nums text-base">{num(data?.total_qty)}</TableCell>
                  <TableCell className="text-slate-500">kg</TableCell>
                  <TableCell className="text-right text-amber-400 font-extrabold tabular-nums text-base">{money(data?.total_imp)}</TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              )}
              {!rows.length && <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-12">Sin compras de gas para el periodo seleccionado.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
